import { useCallback, useEffect, useRef, useState } from "react";
import { Eye, HelpCircle, Loader2, Maximize2, Minimize2, Monitor, Rocket, Smartphone, Tablet, Trash2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { isSuper } from "@/lib/roles";
import { ColorsFonts } from "@/components/admin/ve/ColorsFonts";
import { SectionsPanel } from "@/components/admin/ve/SectionsPanel";
import { ImagesPanel, FilesPanel } from "@/components/admin/ve/MediaPanels";
import { HistoryPanel } from "@/components/admin/ve/HistoryPanel";
import { MenuPanel } from "@/components/admin/ve/MenuPanel";
import { ScaledFrame } from "@/components/admin/ve/ScaledFrame";
import { Tour } from "@/components/admin/ve/Tour";
import { PagePanel } from "@/components/admin/ve/PagePanel";

const DEVICES = [["desktop", Monitor, 1440], ["tablet", Tablet, 768], ["phone", Smartphone, 390]];
const BUILT_IN = [["/", "Home"], ["/about", "About"], ["/sermons", "Messages"], ["/pastors", "Pastors"], ["/events", "Events/Programs"], ["/donate", "Give"], ["/contact", "Contact"]];
const btn = "flex items-center gap-1.5 rounded-full px-4 py-2 font-ui text-xs font-semibold transition-colors disabled:opacity-50";

// Builds the change that puts things back the way they were before `body` was applied.
const inverseOf = (body, d = {}) => {
  const inv = {};
  ["theme", "images", "sections", "hidden", "layouts"].forEach((k) => {
    if (body[k]) inv[k] = Object.fromEntries(Object.keys(body[k]).map((kk) => [kk, d[k]?.[kk] ?? (k === "sections" ? true : "")]));
  });
  if (body.text) inv.text = Object.fromEntries(Object.entries(body.text).map(([l, kv]) => [l, Object.fromEntries(Object.keys(kv).map((kk) => [kk, d.text?.[l]?.[kk] ?? ""]))]));
  if (body.home_order) inv.home_order = d.home_order || [];
  return inv;
};

const useDraft = (frame) => {
  const [state, setState] = useState(null);
  const [undo, setUndo] = useState([]);
  const dataRef = useRef({});
  dataRef.current = state?.data || {};
  const ping = useCallback(() => frame.current?.contentWindow?.postMessage({ type: "ve-reload" }, window.location.origin), [frame]);
  const load = useCallback(() => api.get("/design/draft").then((r) => setState(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))), []);
  useEffect(() => { load(); }, [load]);
  const pushUndo = useCallback((inv) => setUndo((u) => [...u.slice(-49), inv]), []);
  const patch = useCallback(async (body, track = true) => {
    if (track) pushUndo(inverseOf(body, dataRef.current));
    setState((s) => {
      if (!s) return s;
      const d = { ...s.data };
      ["theme", "images", "sections", "hidden", "layouts"].forEach((k) => { if (body[k]) d[k] = { ...(d[k] || {}), ...body[k] }; });
      if (body.home_order) d.home_order = body.home_order;
      return { ...s, data: d };
    });
    try {
      const { data } = await api.put("/design/draft", body);
      setState(data);
      ping();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [ping, pushUndo]);
  const undoLast = useCallback(async () => {
    const last = undo[undo.length - 1];
    if (!last) return;
    setUndo((u) => u.slice(0, -1));
    await patch(last, false);
    toast.success("Undone");
  }, [undo, patch]);
  return { state, setState, load, patch, ping, pushUndo, undoLast, canUndo: undo.length > 0, clearUndo: () => setUndo([]) };
};

const Status = ({ state }) => {
  if (!state) return null;
  if (!state.has_draft || !state.changes) return <span data-testid="ve-status" className="text-xs text-[#4A5568]">No unpublished changes. Everything matches the live website.</span>;
  const when = state.updated_at ? new Date(state.updated_at).toLocaleString() : "";
  return <span data-testid="ve-status" className="text-xs text-[#8A6A12]"><b>{state.changes}</b> unpublished change(s) · saved {when}{state.updated_by ? ` by ${state.updated_by}` : ""}</span>;
};

export const VisualEditor = () => {
  const { user } = useAuth();
  const { pages, refresh, settings } = useSite();
  const [pastors, setPastors] = useState([]);
  useEffect(() => { api.get("/pastors").then((r) => setPastors(Array.isArray(r.data) ? r.data : [])).catch(() => setPastors([])); }, []);
  const frame = useRef(null);
  const { state, setState, load, patch, pushUndo, undoLast, canUndo, clearUndo } = useDraft(frame);
  const [tour, setTour] = useState(() => !localStorage.getItem("aik-ve-tour-done"));
  const [device, setDevice] = useState("desktop");
  const [path, setPath] = useState("/");
  const [src, setSrc] = useState("/?ve=edit");
  const [lang, setLang] = useState(() => localStorage.getItem("aik-lang") || "en");
  const [full, setFull] = useState(false);
  const [mode, setMode] = useState("text");
  const [busy, setBusy] = useState("");
  const data = state?.data || {};
  const master = isSuper(user);
  const deps = Array.isArray(settings?.departments) ? settings.departments : [];
  const pageList = [
    ...BUILT_IN.slice(0, 4),
    ...pastors.map((p) => [`/pastors/${p.slug}`, `— Pastor: ${p.name}`]),
    BUILT_IN[4],
    ...deps.map((d) => [`/events/${d.slug}`, `— ${d.en}`]),
    ...BUILT_IN.slice(5),
    ...(pages || []).map((p) => [`/p/${p.slug}`, p.title_en]),
  ];
  const pageName = (pageList.find(([p]) => p === path)?.[1] || path).replace(/^— /, "");

  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === "ve-path") setPath(e.data.path);
      if (e.data?.type === "ve-saved") { if (e.data.undo) pushUndo(e.data.undo); load(); }
      if (e.data?.type === "ve-busy") setBusy(e.data.busy ? "image" : "");
    };
    window.addEventListener("message", onMsg);
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && !/INPUT|TEXTAREA|SELECT/.test(document.activeElement?.tagName || "")) { e.preventDefault(); undoLast(); }
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("message", onMsg); window.removeEventListener("keydown", onKey); };
  }, [load, pushUndo, undoLast]);

  const go = (p) => { setPath(p); setSrc(`${p}?ve=edit&t=${Date.now()}`); };
  const switchLang = (l) => { setLang(l); frame.current?.contentWindow?.postMessage({ type: "ve-lang", lang: l }, window.location.origin); };
  const switchMode = (m) => { setMode(m); frame.current?.contentWindow?.postMessage({ type: "ve-mode", mode: m }, window.location.origin); };
  const onFrameLoad = () => setTimeout(() => {
    frame.current?.contentWindow?.postMessage({ type: "ve-mode", mode }, window.location.origin);
  }, 600);
  const publish = async () => {
    if (!window.confirm("Publish all draft changes to the live website now?")) return;
    setBusy("publish");
    try {
      await api.post("/design/publish");
      toast.success("Published! Your changes are now live.");
      refresh();
      clearUndo();
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const discard = async () => {
    if (!window.confirm("Throw away all unpublished changes? The website stays as it is now.")) return;
    try {
      await api.delete("/design/draft");
      toast.success("Draft discarded");
      setState(null);
      clearUndo();
      await load();
      go(path);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const width = DEVICES.find(([d]) => d === device)[2];

  return (
    <div data-testid="visual-editor" className={full ? "fixed inset-0 z-50 flex flex-col bg-[#F3ECDD] p-3" : "flex flex-col"}>
      {tour && <Tour onClose={() => { localStorage.setItem("aik-ve-tour-done", "1"); setTour(false); }} />}
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-2xl border border-[#14213D]/10 bg-white p-2.5">
        <select data-testid="ve-page-select" value={pageList.some(([p]) => p === path) ? path : ""} onChange={(e) => go(e.target.value)} className="rounded-full border border-[#14213D]/15 bg-white px-3 py-2 font-ui text-xs text-[#14213D] outline-none">
          {!pageList.some(([p]) => p === path) && <option value="">{path}</option>}
          {pageList.map(([p, l]) => <option key={p} value={p}>{l}</option>)}
        </select>
        <div className="flex rounded-full border border-[#14213D]/15 p-0.5">
          {[["en", "English"], ["mh", "Marshallese"]].map(([l, n]) => <button type="button" key={l} data-testid={`ve-lang-${l}`} onClick={() => switchLang(l)} className={`rounded-full px-3 py-1.5 font-ui text-xs ${lang === l ? "bg-[#14213D] text-white" : "text-[#14213D]"}`}>{n}</button>)}
        </div>
        <div className="flex rounded-full border border-[#14213D]/15 p-0.5">
          {DEVICES.map(([d, Icon]) => <button type="button" key={d} data-testid={`ve-device-${d}`} onClick={() => setDevice(d)} aria-label={d} className={`rounded-full p-2 ${device === d ? "bg-[#14213D] text-white" : "text-[#14213D]"}`}><Icon className="h-3.5 w-3.5" /></button>)}
        </div>
        <div className="flex rounded-full border border-[#2563EB]/30 p-0.5">
          {[["text", "Edit text & photos"], ["select", "Select & remove"]].map(([m, n]) => <button type="button" key={m} data-testid={`ve-mode-${m}`} onClick={() => switchMode(m)} className={`rounded-full px-3 py-1.5 font-ui text-xs ${mode === m ? "bg-[#2563EB] text-white" : "text-[#2563EB]"}`}>{n}</button>)}
        </div>
        <button type="button" data-testid="ve-fullscreen-btn" onClick={() => setFull(!full)} className="rounded-full p-2 text-[#14213D] hover:bg-[#14213D]/5" aria-label="Full screen">{full ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}</button>
        <div className="min-w-0 flex-1 px-2"><Status state={state} /></div>
        <button type="button" data-testid="ve-undo-btn" onClick={undoLast} disabled={!canUndo} title="Undo last change (Ctrl+Z)" className={`${btn} border border-[#14213D]/20 text-[#14213D] hover:bg-[#14213D]/5`}><Undo2 className="h-3.5 w-3.5" /> Undo</button>
        <button type="button" data-testid="ve-tour-btn" onClick={() => setTour(true)} className={`${btn} text-[#2563EB] hover:bg-[#2563EB]/10`}><HelpCircle className="h-3.5 w-3.5" /> Tour</button>
        <button type="button" data-testid="ve-preview-btn" onClick={() => window.open(`${path}?ve=preview`, "_blank", "noopener")} className={`${btn} border border-[#14213D]/20 text-[#14213D] hover:bg-[#14213D]/5`}><Eye className="h-3.5 w-3.5" /> Preview</button>
        <button type="button" data-testid="ve-discard-btn" onClick={discard} disabled={!state?.has_draft} className={`${btn} text-[#B4442A] hover:bg-[#B4442A]/10`}><Trash2 className="h-3.5 w-3.5" /> Discard draft</button>
        {user?.can_publish ? (
          <button type="button" data-testid="ve-publish-btn" onClick={publish} disabled={!state?.changes || busy === "publish"} className={`${btn} bg-[#C8862F] px-5 text-white hover:bg-[#B37425]`}>
            {busy === "publish" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Rocket className="h-3.5 w-3.5" />} PUBLISH
          </button>
        ) : (
          <span data-testid="ve-publish-note" className="text-xs text-[#4A5568]">Someone allowed to publish will review and publish your changes.</span>
        )}
      </div>
      <div className={`grid min-h-0 flex-1 gap-3 lg:grid-cols-[1fr_340px] ${full ? "" : "lg:h-[78vh]"}`}>
        <div className="relative min-h-[70vh] overflow-hidden rounded-2xl border border-[#14213D]/10 bg-[#E9E2D2] lg:min-h-0">
          <ScaledFrame ref={frame} width={width} src={src} onLoad={onFrameLoad} />
          {busy === "image" && <div className="absolute inset-0 grid place-items-center bg-white/60"><Loader2 className="h-8 w-8 animate-spin text-[#C8862F]" /></div>}
        </div>
        <aside className="min-h-0 overflow-y-auto rounded-2xl border border-[#14213D]/10 bg-[#FBF8F2] p-4" data-lenis-prevent>
          <p className="mb-3 rounded-xl bg-[#C8862F]/10 p-3 text-xs leading-relaxed text-[#14213D]"><b>How to edit:</b> in <b>Edit text & photos</b> mode, click any text to change it or any photo to replace it. In <b>Select & remove</b> mode, click any block to remove or replace it. Use the <b>This page</b> tab to add headings, text, announcements, photos and videos to the page you are on, or to drag its sections into a new order. Pick any page, including each pastor and each Events/Programs department, from the page list at the top. Every change saves instantly as a draft. Visitors only see it after you press <b>PUBLISH</b>.</p>
          <Tabs defaultValue="page">
            <TabsList className="grid h-auto w-full grid-cols-4 gap-1">
              <TabsTrigger value="page" data-testid="ve-tab-page" className="px-1 text-xs">This page</TabsTrigger>
              <TabsTrigger value="menu" data-testid="ve-tab-menu" className="px-1 text-xs">Menu</TabsTrigger>
              <TabsTrigger value="style" data-testid="ve-tab-style" className="px-1 text-xs">Style</TabsTrigger>
              <TabsTrigger value="sections" data-testid="ve-tab-sections" className="px-1 text-xs">Sections</TabsTrigger>
              <TabsTrigger value="images" data-testid="ve-tab-images" className="px-1 text-xs">Images</TabsTrigger>
              <TabsTrigger value="files" data-testid="ve-tab-files" className="px-1 text-xs">Files</TabsTrigger>
              {master && <TabsTrigger value="history" data-testid="ve-tab-history" className="px-1 text-xs">History</TabsTrigger>}
            </TabsList>
            <TabsContent value="page" className="pt-3"><PagePanel key={path} path={path} pageName={pageName} data={data} onPatch={patch} /></TabsContent>
            <TabsContent value="menu" className="pt-3"><MenuPanel text={data.text} onPatch={patch} /></TabsContent>
            <TabsContent value="style" className="pt-3"><ColorsFonts theme={data.theme} onTheme={(t) => patch({ theme: t })} /></TabsContent>
            <TabsContent value="sections" className="pt-3"><SectionsPanel data={data} onPatch={patch} /></TabsContent>
            <TabsContent value="images" className="pt-3"><ImagesPanel images={data.images} onPatch={patch} /></TabsContent>
            <TabsContent value="files" className="pt-3"><FilesPanel /></TabsContent>
            {master && <TabsContent value="history" className="pt-3"><HistoryPanel key={state?.updated_at || "h"} onRestored={() => { refresh(); load(); go(path); }} /></TabsContent>}
          </Tabs>
        </aside>
      </div>
    </div>
  );
};
